import type { AudioEngine, AudioPlaybackRequest, AudioTrack } from './audio-service';

/**
 * The one place in the app that touches the browser's audio stack (`tests/architecture/audio-boundaries.test.ts`
 * pins that).
 *
 * **Why WebAudio and a media element, rather than either alone.**
 *  - A bare `HTMLAudioElement` cannot ramp: `volume` is a step, and stepping a music bed under
 *    speech is an audible click at both ends. The ducking contract asks for an attack and a
 *    release, and `AudioParam.linearRampToValueAtTime` is the only thing in a browser that gives
 *    them honestly.
 *  - A bare `AudioBufferSourceNode` would mean fetching and decoding every file up front, which
 *    adds a `fetch` (and therefore a CSP `connect-src` conversation) and a decode step to a slice
 *    whose whole point is the pipe.
 *  - `createMediaElementSource` composes the two: the element loads and loops the file exactly as
 *    an `<audio>` tag would — a media load, so the CSP is untouched — and the graph after it gives
 *    the gain ramps. `AudioContext.resume()` is also precisely the autoplay unlock primitive, so
 *    the grown-up's existing gesture is the only gesture there is.
 *
 * **Nothing here may throw (S0-R1).** Every call below is made, directly or indirectly, from the
 * mission runtime's event path. The browser audio stack throws for perfectly ordinary reasons — an
 * element already routed into a graph, an element whose metadata has not loaded, a context that has
 * been closed — and every one of those is a *silence* problem. Letting one escape would make it a
 * *round* problem. So each entry point is guarded, a graph that refuses to be built yields an inert
 * track, and the worst case a family can reach is a quiet mission.
 *
 * **Release is not optional.** A media element that is merely paused keeps its buffered file and
 * its node in the graph. Every stop detaches the nodes, drops the source and reloads the element
 * empty, and `close()` releases the context. Sound is held to the same rule as camera tracks: it
 * ends with the sitting.
 */

/** The slice of `AudioParam` used here. */
export interface AudioParamLike {
  value: number;
  cancelScheduledValues(at: number): void;
  setValueAtTime(value: number, at: number): void;
  linearRampToValueAtTime(value: number, at: number): void;
}

export interface GainNodeLike {
  readonly gain: AudioParamLike;
  connect(destination: unknown): unknown;
  disconnect(): void;
}

export interface MediaElementSourceLike {
  connect(destination: unknown): unknown;
  disconnect(): void;
}

export interface AudioElementLike {
  loop: boolean;
  currentTime: number;
  src: string;
  play(): Promise<void> | void;
  pause(): void;
  load(): void;
  addEventListener(type: 'ended', listener: () => void): void;
  removeEventListener(type: 'ended', listener: () => void): void;
}

export interface AudioContextLike {
  readonly currentTime: number;
  readonly destination: unknown;
  createGain(): GainNodeLike;
  createMediaElementSource(element: AudioElementLike): MediaElementSourceLike;
  resume(): Promise<void>;
  close(): Promise<void>;
}

export interface WebAudioEngineOptions {
  /** Injected in tests; production builds the browser's own `AudioContext`. */
  contextFactory?: () => AudioContextLike;
  /** Injected in tests; production builds an `Audio` element. */
  elementFactory?: (source: string) => AudioElementLike;
}

function browserContext(): AudioContextLike {
  const Ctor = (
    globalThis as {
      AudioContext?: new () => AudioContextLike;
      webkitAudioContext?: new () => AudioContextLike;
    }
  ).AudioContext;
  if (Ctor === undefined) {
    throw new Error('This browser has no Web Audio support.');
  }
  return new Ctor();
}

function browserElement(source: string): AudioElementLike {
  const Ctor = (globalThis as { Audio?: new (source: string) => AudioElementLike }).Audio;
  if (Ctor === undefined) {
    throw new Error('This browser has no audio element.');
  }
  const element = new Ctor(source);
  // Same-origin theme assets only, and never credentialed: nothing here may become a request that
  // carries anything about a family.
  return element;
}

/** True when this environment can make sound at all. Checked before a context is ever built. */
export function webAudioAvailable(): boolean {
  const scope = globalThis as { AudioContext?: unknown; Audio?: unknown };
  return scope.AudioContext !== undefined && scope.Audio !== undefined;
}

/**
 * Builds the engine, or refuses with `null` (R1-1).
 *
 * {@link webAudioAvailable} only asks whether the constructors exist. Everything this function does
 * is real browser work that fails for real reasons — a device with no output, a page that has
 * exhausted its context allowance, a driver that went away — and all of it used to run unguarded
 * inside the app's composition root, where a thrown `AudioContext` took the whole sitting down
 * before a route had mounted. That is the S0-R1 invariant in its largest form: an audio failure may
 * cost a family the music, never the game.
 *
 * A context that got as far as existing is **closed** on the way out. It is a scarce shared browser
 * resource, and a page that leaks one on every failed attempt eventually cannot make sound even
 * when the device could.
 *
 * The refusal is expressed here rather than at the caller because this file is the only one allowed
 * to touch the browser's audio stack (`tests/architecture/audio-boundaries.test.ts`): the composition
 * root must not be the thing constructing or closing an `AudioContext`.
 */
export function createWebAudioEngine(options: WebAudioEngineOptions = {}): AudioEngine | null {
  let context: AudioContextLike;
  try {
    context = (options.contextFactory ?? browserContext)();
  } catch {
    return null;
  }

  const createElement = options.elementFactory ?? browserElement;

  let master: GainNodeLike;
  try {
    master = context.createGain();
    master.connect(context.destination);
  } catch {
    // Built, then unusable. Give the context back rather than orphaning it.
    try {
      void Promise.resolve(context.close()).catch(() => undefined);
    } catch {
      // A context that objects to being closed is already going away with the page.
    }
    return null;
  }

  const liveStops = new Set<() => void>();
  let closed = false;

  /** Runs browser work that is allowed to fail. Failure is silence, never an exception. */
  const quietly = (work: () => void): void => {
    try {
      work();
    } catch {
      // A browser audio object that objects is a lost sound. It is never a lost round (S0-R1).
    }
  };

  /** Applies a gain change: ramped when asked for, held immediately when not. */
  const applyGain = (param: AudioParamLike, value: number, rampMs: number): void => {
    quietly(() => {
      const now = context.currentTime;
      if (rampMs <= 0) {
        param.cancelScheduledValues(now);
        param.setValueAtTime(value, now);
        return;
      }
      param.cancelScheduledValues(now);
      // Anchor at the value it has right now, so the ramp starts from what is audible rather than
      // from whatever the last schedule left behind.
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(value, now + rampMs / 1000);
    });
  };

  return {
    async resume() {
      if (closed) return;
      await context.resume();
    },

    setMasterGain(gain, rampMs) {
      if (closed) return;
      applyGain(master.gain, gain, rampMs);
    },

    play(request: AudioPlaybackRequest): AudioTrack | null {
      if (closed) return null;

      /**
       * Building the graph is the part most likely to refuse (S0-R1).
       *
       * `createMediaElementSource` throws `InvalidStateError` for an element already routed, and a
       * context that has failed throws from `createGain`. A caller on the gameplay path gets a
       * refusal and carries on; it has lost a sound, not a round.
       *
       * The refusal used to be an inert track, which is the same object a *playing* sound hands
       * back (R1-3). Returning nothing is the honest answer: there is no sound here, so the service
       * lists none, and a caller with another way to be heard takes it.
       */
      /**
       * Construction is transactional (R2-5).
       *
       * The pieces are recorded as they are acquired, so a step that refuses can unwind the ones
       * before it. Without that, a graph that failed on its *last* hop — element built, source
       * created, gain created and connected — returned an honest `null` while leaving the element
       * holding its file and both nodes sitting in the graph. One accumulated set per attempt, for
       * the lifetime of the page.
       */
      let element: AudioElementLike | null = null;
      let source: MediaElementSourceLike | null = null;
      let gain: GainNodeLike | null = null;

      /** Undoes whatever was acquired, each step independently (the `stop` discipline). */
      const unwind = (): void => {
        quietly(() => {
          source?.disconnect();
        });
        quietly(() => {
          gain?.disconnect();
        });
        quietly(() => {
          element?.pause();
        });
        quietly(() => {
          if (element === null) return;
          element.src = '';
          element.load();
        });
      };

      try {
        element = createElement(request.source);
        element.loop = request.loop;
        source = context.createMediaElementSource(element);
        gain = context.createGain();
        gain.gain.value = request.gain;
        source.connect(gain);
        /**
         * Two buses, one graph (A-R1).
         *
         * The master gain is what the grown-up's **Sound** control moves, and that control names
         * music and chimes. A spoken line is connected straight to the destination so that turning
         * the music off does not turn the voice off — its own gain node still carries narration
         * volume and the duck, which is everything speech actually needs from the graph.
         */
        gain.connect(request.bus === 'speech' ? context.destination : master);
      } catch {
        unwind();
        return null;
      }

      /**
       * Past this point the graph is whole, so the three pieces are no longer nullable.
       *
       * Captured into `const`s rather than re-asserted at each use: everything below closes over
       * them for the life of the track, and a `null` there would mean this block never ran.
       */
      const builtElement: AudioElementLike = element;
      const builtSource: MediaElementSourceLike = source;
      const builtGain: GainNodeLike = gain;

      let stopped = false;
      const stop = (): void => {
        if (stopped) return;
        stopped = true;
        liveStops.delete(stop);
        /**
         * Each release step is independent (S0-R1).
         *
         * One `pause()` that objects must not strand the disconnect and the source drop behind it,
         * because those are the steps that actually let the file and the nodes go. Grouping them
         * under one `try` would have made the first failure keep the resource alive for the
         * lifetime of the page.
         */
        quietly(() => {
          builtElement.removeEventListener('ended', onEnded);
        });
        quietly(() => {
          builtElement.pause();
        });
        quietly(() => {
          builtSource.disconnect();
        });
        quietly(() => {
          builtGain.disconnect();
        });
        // Dropping the source and reloading is what actually releases the buffered file; a paused
        // element holds on to it for the lifetime of the page.
        quietly(() => {
          builtElement.src = '';
          builtElement.load();
        });
      };
      liveStops.add(stop);

      /**
       * A sound that reached its own end is released here, not by whoever might stop it later
       * (R1-4).
       *
       * Nobody stops it later. The service drops its logical entry the moment this fires, so its
       * `stop()` can never reach this track again — and the element, its buffered file, both nodes
       * and the entry in `liveStops` would then live until the whole provider unmounted, once per
       * play. `stop` is idempotent by its own guard, so running it here costs nothing on the paths
       * where something *did* stop the sound first, and the caller is told afterwards: the
       * notification is what may start the next line, and it should find this one already gone.
       */
      const onEnded = (): void => {
        stop();
        request.onEnded?.();
      };
      quietly(() => {
        builtElement.addEventListener('ended', onEnded);
      });

      /**
       * A start the browser refuses *after* accepting the graph (R1-3).
       *
       * `HTMLMediaElement.play()` rejects for ordinary reasons — an unsupported codec, a file that
       * is not there, an autoplay policy that has not been satisfied — and this rejection used to
       * be swallowed whole. The sound was then listed as playing for the rest of the sitting and a
       * baked line waited out its entire watchdog in silence.
       *
       * A rejection that arrives *after* something stopped this track is not a refusal: pausing an
       * element with a pending `play()` rejects it with `AbortError` in every browser, so reporting
       * that would turn every interruption into a phantom failure. The `stopped` guard is the whole
       * of that distinction. Either way this is still silence, never an exception on the round path.
       */
      /**
       * A start the browser refuses **synchronously** is a refusal too (R2-4).
       *
       * `play()` is entitled to throw rather than reject — a detached element, a media stack that
       * has gone. That throw used to be swallowed by the same guard that keeps the round safe, and
       * the track was handed back as though it had started: the service listed it, and a baked line
       * waited out its whole watchdog before settling as `stopped`, which is the one end reason
       * that does *not* fall back to the browser voice. Refusing here — before the caller is given
       * anything to register — means there is nothing to list and nothing to correct later.
       */
      try {
        void Promise.resolve(builtElement.play()).then(
          () => undefined,
          () => {
            if (stopped) return;
            stop();
            quietly(() => {
              request.onRefused?.();
            });
          },
        );
      } catch {
        // `stop()` is the same release the rest of the file uses, and it also takes this track out
        // of `liveStops`. No `onRefused`: the refusal is the return value, and reporting it twice
        // would have the service settle a waiter it never created.
        stop();
        return null;
      }

      return {
        setGain(value, rampMs) {
          if (stopped) return;
          applyGain(builtGain.gain, value, rampMs);
        },
        restart() {
          if (stopped) return;
          // `currentTime = 0` throws for an element whose metadata has not loaded — ordinary on a
          // slow first play, and never a reason to interrupt anything (S0-R1).
          quietly(() => {
            builtElement.currentTime = 0;
          });
          quietly(() => {
            void Promise.resolve(builtElement.play()).catch(() => {
              // As above.
            });
          });
        },
        stop,
      };
    },

    close() {
      if (closed) return;
      closed = true;
      for (const stop of [...liveStops]) stop();
      liveStops.clear();
      quietly(() => {
        master.disconnect();
      });
      void Promise.resolve(context.close()).catch(() => {
        // A context that objects to being closed is already going away with the page.
      });
    },
  };
}
